<template>
  <nav class="menu" :class="cssClasses">
    <div class="menu_holder">
      <button
        class="menu_control"
        @click="open"
      >
        {{ $t('Меню') }}
      </button>
      <div class="menu_wrap" :class="{'menu_wrap__assembly': !isAnimated}">
        <ul class="menu_list">
          <RouterLink
            class="menu_item menu_item__main" tag="li"
            :to="{ name: 'dashboard' }"
            @click.native="onMenuItemClick"
            exact
          >
            <a class="menu_link">{{ $t('Главная') }}</a>
          </RouterLink>
          <li class="menu_item">
            <a
              class="menu_link menu_link__custom"
              v-if="isOnlyOnePartnerAvailable && depositUrl"
              :href="depositUrl"
              target="_blank"
              @click="onMenuItemClick"
            >
              {{ $t('Пополнить карту') }}
            </a>
            <a
              class="menu_link menu_link__custom"
              v-else
              href="#"
              @click.prevent="showPartnersOverlay"
            >
              {{ $t('Пополнить карту') }}
            </a>
          </li>
          <RouterLink
            class="menu_item" tag="li"
            :to="{ name: 'about' }"
            @click.native="onMenuItemClick"
          >
            <a class="menu_link">{{ $t('О карте') }}</a>
          </RouterLink>
          <RouterLink
            class="menu_item" tag="li"
            :to="{ name: 'cards' }"
            @click.native="onMenuItemClick"
          >
            <a class="menu_link">{{ $t('Мои карты') }}</a>
          </RouterLink>
          <RouterLink
            class="menu_item menu_item__quest" tag="li"
            :to="{ name: 'random-rewards' }"
            v-if="randomButtonData
              && (randomButtonData.status === RANDOM_BUTTON_STATUS.ACTIVE
                  || randomButtonData.status === RANDOM_BUTTON_STATUS.FINISH)"
            @click.native="onMenuItemClick"
          >
            <a class="menu_link menu_link__quest">{{ $t('Blitz квест') }}</a>
          </RouterLink>
        </ul>
        <button
          class="menu_close"
          v-if="isMenuOpened"
          @click="close"
        >
          Close
        </button>
      </div>
      <RouterLink class="menu_logo" :to="{ name: 'dashboard' }" exact @click.native="onMenuItemClick">
        {{ $t('Карта Леста') }}
      </RouterLink>
      <button class="menu_cm-switch" @click="isCMOpened = !isCMOpened">Lesta.ru</button>
    </div>
    <Portal
      to="overlay"
      v-if="isPartnersSelectOverlayOpened"
    >
      <Overlay @close="closePartnersOverlay">
        <RefillPartnerOverlay />
      </Overlay>
    </Portal>
  </nav>
</template>

<script>
import { Vue, Component, Watch } from 'vue-property-decorator'
import { State, Mutation } from 'vuex-class'
import { getDepositUrl } from '@/helpers'
import { DEFAULT_PARTNER_NAME_FOR_COUNTRY, AVAILABLE_PARTNERS, RANDOM_BUTTON_STATUS } from '@/constants'
import RefillPartnerOverlay from '@/components/RefillPartnerOverlay/RefillPartnerOverlay'
import Overlay from '@/common/components/Overlay.vue'
import throttle from 'lodash/throttle'

@Component({
  components: { RefillPartnerOverlay, Overlay }
})
class MainMenu extends Vue {
  @State('country') currentCountry
  @State('randomButtonData') randomButtonData
  @State(({ offers }) => offers.meta.totalCount) offersCount
  @Mutation('ui/SET_SCROLL_LOCK') setScrollLock
  @Mutation('ui/TOGGLE_COMMON_MENU') toggleCommonMenu

  RANDOM_BUTTON_STATUS = RANDOM_BUTTON_STATUS

  isMenuOpened = false
  isCMOpened = false
  isAnimated = false

  isFixed = false
  isAssembled = false
  isShown = false
  isPartnersSelectOverlayOpened = false

  lastScrollTop = undefined
  initialOffset = undefined

  mounted () {
    this.$nextTick().then(() => {
      const domRect = this.$el.getBoundingClientRect()
      const cm = document.querySelector('#common_menu')
      let cmHeight = 37
      if (cm) {
        cmHeight = cm.getBoundingClientRect().height || cmHeight
      }
      this.initialTop = cmHeight
      this.initialOffset = cmHeight + domRect.height

      this.throttledCheckScroll = throttle(this.checkScroll, 10)
      window.addEventListener('scroll', this.checkScroll)
    })
  }

  beforeDestroy () {
    window.removeEventListener('scroll', this.throttledCheckScroll)
  }

  get cssClasses () {
    return {
      'menu__opened': this.isMenuOpened,
      'menu__cm-show': this.isCMOpened,
      'menu__fixed': this.isFixed,
      'menu__show': this.isShown,
      'menu__fixed-assembly': this.isAssembled && !this.isShown && !this.isFixed
    }
  }

  checkScroll () {
    const { pageYOffset } = window
    this.$nextTick().then(() => {
      this.isAssembled = pageYOffset >= this.initialOffset
      this.isShown = pageYOffset < this.lastScrollTop && pageYOffset >= this.initialTop
      this.isFixed = pageYOffset > this.lastScrollTop && this.lastScrollTop > this.initialOffset

      this.lastScrollTop = pageYOffset
    })
  }

  get depositUrl () {
    if (this.currentCountry === null) return null
    return getDepositUrl(DEFAULT_PARTNER_NAME_FOR_COUNTRY[this.currentCountry])
  }

  get isOnlyOnePartnerAvailable () {
    if (this.currentCountry === null) return null
    return Object.keys(AVAILABLE_PARTNERS[this.currentCountry]).length === 1
  }

  onMenuItemClick (event) {
    this.close()
    this.$analytics.trackMainMenuItemClick({ screenId: this.$route.name, linkUrl: event.target.href })
  }

  showPartnersOverlay () {
    this.isPartnersSelectOverlayOpened = true
    this.$router.push({ hash: 'partners-overlay' })
  }

  closePartnersOverlay () {
    this.isPartnersSelectOverlayOpened = false
    if (this.$route.hash === '#partners-overlay') {
      this.$router.replace({ hash: '' })
    }
  }

  open () {
    this.isMenuOpened = true
    this.isAnimated = true
    this.setScrollLock(true)
  }

  close () {
    const delay = 400 // x2 animation duration in css

    this.isMenuOpened = false
    this.setScrollLock(false)

    setTimeout(() => {
      this.isAnimated = false
    }, delay)
  }

  @Watch('isCMOpened')
  onShowCommomMenuChanged (isOpen) {
    this.toggleCommonMenu(isOpen)
  }

  @Watch('$route')
  closeOverlayOnRouterChange () {
    if (this.$route.hash !== '#partners-overlay') {
      this.isPartnersSelectOverlayOpened = false
    } else {
      this.isPartnersSelectOverlayOpened = true
    }
  }
}

export default MainMenu
</script>

<style lang="scss" src="./styles.scss" />
<style lang="scss">
.menu_link {
  &__quest {
    color: rgba(#FF002A, .85);
    &:hover {
      color: #FF002A;
    }
    @at-root .menu_item.active & {
      color: #FF002A;
    }
  }
}
.menu_item {
  &__quest {
    &.active {
      &:after {
        background-color: #FF002A;
      }
    }
  }
}
</style>
